<script setup>
import { onMounted, ref } from 'vue';

const props = defineProps({
    color: {
        type: String,
    },
    width: {
        type: [String, Number],
        default: 2,
    },
    animate: {
        type: Boolean,
        default: false,
    },
    animateDuration: {
        type: [String, Number],
        default: 5,
    },
    pauseAnimation: {
        type: Boolean,
        default: false,
    },
    fill: {
        type: Boolean,
        default: true,
    }
});

defineEmits(['animation-end']);

const svg = ref();
const perimeter = ref(0);
const borderRadius = ref({ rx: 0, ry: 0 });

onMounted(() => {
    const parent = svg.value.parentElement;

    if (!parent) {
        throw new Error('BorderFill must be used within a parent element');
    }

    // Get the computed styles
    const computedStyles = window.getComputedStyle(parent);
    const rect = parent.getBoundingClientRect();

    // **BETTER APPROACH: Use getComputedStyle for individual border-radius properties**
    // This gives us the actual computed values that CSS uses
    const borderTopLeftRadius = computedStyles.borderTopLeftRadius;
    const borderTopRightRadius = computedStyles.borderTopRightRadius;
    const borderBottomLeftRadius = computedStyles.borderBottomLeftRadius;
    const borderBottomRightRadius = computedStyles.borderBottomRightRadius;

    // Parse the actual computed radius (already constrained by CSS)
    const parseRadius = (radiusValue) => {
        if (!radiusValue || radiusValue === 'none' || radiusValue === '0px') return 0;
        return parseFloat(radiusValue);
    };

    // Get the actual computed radii
    const topLeftRadius = parseRadius(borderTopLeftRadius);
    const topRightRadius = parseRadius(borderTopRightRadius);
    const bottomLeftRadius = parseRadius(borderBottomLeftRadius);
    const bottomRightRadius = parseRadius(borderBottomRightRadius);

    // Use the average or maximum radius for the rect
    // For uniform rounded buttons, they should all be the same
    const rx = Math.max(topLeftRadius, topRightRadius, bottomLeftRadius, bottomRightRadius);
    const ry = rx; // For rounded-full, rx and ry should be equal

    borderRadius.value = { rx, ry };

    // Calculate perimeter accounting for rounded corners
    const straightWidth = Math.max(0, rect.width - 2 * rx);
    const straightHeight = Math.max(0, rect.height - 2 * ry);

    // More accurate perimeter calculation for rounded rectangles
    const cornerCircumference = 2 * Math.PI * Math.min(rx, ry); // Use the smaller radius

    perimeter.value = 2 * straightWidth + 2 * straightHeight + cornerCircumference;
});
</script>

<style lang="css" scoped>
svg {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    pointer-events: none;
    border-radius: inherit;
}

rect {
  fill: none;
  stroke: var(--border-fill-color);
  stroke-width: var(--border-fill-width);
  stroke-dasharray: var(--border-fill-perimeter);
  stroke-dashoffset: var(--border-fill-perimeter);
}

rect[data-animate=true] {
  animation: border-fill var(--border-fill-animate-duration) ease-out forwards;
}

rect[data-animate=false][data-fill=true] {
  stroke-dashoffset: 0;
}

rect[data-animate=true][data-pause-animation=true] {
    animation-play-state: paused;
}

@keyframes border-fill {
  0% {
    stroke-dashoffset: var(--border-fill-perimeter);
  }
  100% {
    stroke-dashoffset: 0;
  }
}
</style>

<template>
    <svg aria-hidden="true" ref="svg" :style="{
        '--border-fill-perimeter': perimeter,
        '--border-fill-color': color,
        '--border-fill-width': width,
        '--border-fill-animate-duration': animateDuration+'s',
    }">
        <rect
            :data-fill="fill"
            :data-animate="animate"
            :data-pause-animation="pauseAnimation"
            width="100%"
            height="100%"
            :rx="borderRadius.rx"
            :ry="borderRadius.ry"
            @animationend="() => $emit('animation-end')"
        />
    </svg>
</template>
